const CAPTCHA_DIGIT_LEN = 4;

interface CaptchaInputProps {
  value: string;
  onChange: (val: string) => void;
  onValidChange: (valid: boolean) => void;
  /** Remote `captcha_text` (normalized to 4 digits for comparison) */
  expectedAnswer: string;
  /** `img` src (e.g. data URL from `image_base64`) */
  imageSrc: string | null;
  loading?: boolean;
  loadError?: string | null;
  onRefresh: () => void;
  error?: string;
  inputId?: string;
}

export default function CaptchaInput({
  value,
  onChange,
  onValidChange,
  expectedAnswer,
  imageSrc,
  loading = false,
  loadError = null,
  onRefresh,
  error,
  inputId = 'captcha-input',
}: CaptchaInputProps) {
  const normalizeExpectedDigits = (raw: string) =>
    raw.replace(/\D/g, '').slice(0, CAPTCHA_DIGIT_LEN);

  /** Compare 4-digit input with remote `captcha_text` (digits only). */
  const runCaptchaValidation = (digits: string) => {
    const expected = normalizeExpectedDigits(expectedAnswer);
    const filled = digits.length === CAPTCHA_DIGIT_LEN;
    const ok = filled && expected.length === CAPTCHA_DIGIT_LEN && digits === expected;
    onValidChange(ok);
  };

  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, CAPTCHA_DIGIT_LEN);
    onChange(digits);
    runCaptchaValidation(digits);
  };

  const handleBlur = () => {
    if (loading || !imageSrc) return;
    const digits = value.replace(/\D/g, '').slice(0, CAPTCHA_DIGIT_LEN);
    if (digits !== value) onChange(digits);
    runCaptchaValidation(digits);
  };

  return (
    <div>
      <label className="block text-xs font-normal text-gray-600 mb-1.5">Captcha</label>
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => (loadError || !loading) && onRefresh()}
          className={`relative h-9 w-[130px] shrink-0 overflow-hidden rounded-lg border bg-gray-50 flex items-center justify-center ${
            error || loadError ? 'border-red-200' : 'border-gray-200'
          } ${loading ? 'cursor-wait' : 'cursor-pointer'}`}
          title="Click to refresh"
          aria-label="Captcha image, click to refresh"
        >
          {loading && (
            <span className="text-gray-400">
              <i className="ri-loader-4-line animate-spin text-lg" />
            </span>
          )}
          {!loading && imageSrc && (
            <img src={imageSrc} alt="Captcha" className="h-full w-full object-cover" draggable={false} />
          )}
          {!loading && !imageSrc && !loadError && (
            <span className="text-[10px] text-gray-400 px-1 text-center">No image</span>
          )}
          {!loading && loadError && <span className="text-[10px] text-red-500 px-1 text-center leading-tight">Error</span>}
        </button>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
          title="Refresh captcha"
          aria-label="Refresh captcha"
        >
          <i className="ri-refresh-line text-base" />
        </button>
        <div className="relative min-w-0 flex-1">
          <input
            id={inputId}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder="Captcha Code"
            maxLength={CAPTCHA_DIGIT_LEN}
            autoComplete="off"
            disabled={loading || !imageSrc}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : loadError ? `${inputId}-load-error` : undefined}
            className={`w-full rounded-lg border bg-gray-50 px-3 py-2.5 text-sm font-normal tabular-nums tracking-widest transition focus:outline-none placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
              error
                ? 'border-red-300 focus:border-red-400 focus:ring-1 focus:ring-red-100'
                : 'border-gray-200 focus:border-green-500 focus:ring-1 focus:ring-green-200'
            }`}
          />
        </div>
      </div>
      {loadError && (
        <p id={`${inputId}-load-error`} className="mb-1 text-xs text-red-600">
          {loadError}
        </p>
      )}
      <p className="text-xs text-gray-400">
        Enter the {CAPTCHA_DIGIT_LEN}-digit code from the image. It is validated on blur and when {CAPTCHA_DIGIT_LEN}{' '}
        digits are entered.
      </p>
    </div>
  );
}
